import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, Plus, FileText, Upload, Trash2, Loader2,
  FileCheck, Camera, Download, Eye, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const DOCUMENT_TYPES = [
  { value: 'bol', label: 'Bill of Lading' },
  { value: 'rate_confirmation', label: 'Rate Confirmation' },
  { value: 'lumper_receipt', label: 'Lumper Receipt' },
  { value: 'scale_ticket', label: 'Scale Ticket' },
  { value: 'delivery_receipt', label: 'Delivery Receipt' },
  { value: 'other', label: 'Other Document' },
];

const DriverDocumentsPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    document_type: 'bol',
    document_name: '',
    notes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    if (!token) {
      navigate('/driver/login');
      return;
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchDocuments();
  }, [navigate]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await api.get('/driver/documents');
      setDocuments(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/driver/login');
      } else {
        toast.error('Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!formData.document_name.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/driver/documents', {
        document_type: formData.document_type,
        document_name: formData.document_name,
        notes: formData.notes || null,
        load_id: null
      });

      toast.success('Document record created');
      setShowUploadDialog(false);
      setFormData({ document_type: 'bol', document_name: '', notes: '' });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;
    
    try {
      await api.delete(`/driver/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const found = DOCUMENT_TYPES.find(d => d.value === type);
    return found ? found.label : type;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Group documents by type
  const groupedDocs = documents.reduce((acc, doc) => {
    const type = doc.document_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071423] pb-24">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/driver/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-white">Documents</h1>
              <p className="text-xs text-gray-400">Scan & store documents</p>
            </div>
          </div>
          <Button 
            size="sm" 
            className="bg-[#4ADE80] hover:bg-[#22C55E] text-black"
            onClick={() => setShowUploadDialog(true)}
          >
            <Camera className="w-4 h-4 mr-1" />
            Scan
          </Button>
        </div>
      </div>

      {/* Stats Card */}
      <div className="px-4 py-4">
        <Card className="border-[#3B82F6]/20 bg-gradient-to-r from-[#0D1B2A] to-[#112240]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">Total Documents</p>
                <p className="text-2xl font-bold text-white">{documents.length}</p>
              </div>
              <FileText className="w-10 h-10 text-[#3B82F6]" />
            </div>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(groupedDocs).slice(0, 3).map(([type, docs]) => (
                <div key={type} className="bg-[#0A1628] px-2 py-1 rounded text-xs">
                  <span className="text-gray-400">{getDocumentTypeLabel(type)}:</span>
                  <span className="text-white ml-1">{docs.length}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documents List */}
      <div className="px-4">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <FileCheck className="w-4 h-4" />
          Scanned Documents
        </h2>

        {documents.length === 0 ? (
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-500 mb-3" />
              <p className="text-gray-400">No documents scanned yet</p>
              <p className="text-xs text-gray-500 mt-1">Tap the Scan button to add your first document</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <Card key={doc.id} className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#112240] flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#3B82F6]" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{doc.document_name}</p>
                        <p className="text-xs text-[#4ADE80]">{getDocumentTypeLabel(doc.document_type)}</p>
                        <p className="text-xs text-gray-500">{formatDate(doc.scanned_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {doc.document_url && doc.document_url !== 'pending_upload' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(doc.document_url, '_blank')}
                        >
                          <Eye className="w-4 h-4 text-blue-400" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {doc.notes && (
                    <p className="text-xs text-gray-400 mt-2 pl-13">{doc.notes}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>Scan Document</DialogTitle>
            <DialogDescription>Add a new document record</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddDocument} className="space-y-4">
            <div>
              <Label>Document Type</Label>
              <Select 
                value={formData.document_type} 
                onValueChange={(val) => setFormData({...formData, document_type: val})}
              >
                <SelectTrigger className="mt-1 bg-[#0A1628] border-[#3B82F6]/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0D1B2A] border-[#3B82F6]/20">
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Document Name</Label>
              <Input
                value={formData.document_name}
                onChange={(e) => setFormData({...formData, document_name: e.target.value})}
                placeholder="e.g., BOL #12345"
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                required
              />
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
              />
            </div>

            {/* Photo capture placeholder */}
            <div className="border-2 border-dashed border-[#3B82F6]/30 rounded-lg p-6 text-center">
              <Camera className="w-10 h-10 mx-auto text-gray-500 mb-2" />
              <p className="text-sm text-gray-400">Camera capture coming soon</p>
              <p className="text-xs text-gray-500 mt-1">Document will be stored as a reference</p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowUploadDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Document'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDocumentsPage;
